/**
 * Load Data Management Component
 * Admin component for uploading Excel files and managing load data
 */
import { useState, useEffect } from 'react';
import api from '../utils/api';

function LoadDataManagement({ companies }) {
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [loads, setLoads] = useState(null);
  const [loadingLoads, setLoadingLoads] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (selectedCompanyId) {
      loadLoads();
    } else {
      setLoads(null);
    }
  }, [selectedCompanyId, page]);

  const loadLoads = async () => {
    if (!selectedCompanyId) return;
    setLoadingLoads(true);
    try {
      const response = await api.get(`/loads/${selectedCompanyId}?page=${page}&limit=20`);
      setLoads(response.data);
    } catch (error) {
      console.error('Failed to load loads:', error);
      setLoads(null);
    } finally {
      setLoadingLoads(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedCompanyId || !file) {
      alert('Please select a company and file');
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/loads/${selectedCompanyId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult(response.data);
      setFile(null);
      // Reset file input
      const fileInput = document.getElementById('load-file-input');
      if (fileInput) fileInput.value = '';
      // Reload loads list
      loadLoads();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Upload failed';
      setUploadResult({ success: false, message: detail, errors: [detail] });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!selectedCompanyId) return;
    const companyName = companies.find(c => String(c.company_id) === String(selectedCompanyId))?.company_name || selectedCompanyId;
    if (!window.confirm(`Delete ALL load data for "${companyName}"? This cannot be undone.`)) return;

    setDeleting(true);
    try {
      const response = await api.delete(`/loads/${selectedCompanyId}`);
      alert(response.data.message || 'Loads deleted successfully');
      setLoads(null);
      setPage(1);
      loadLoads();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete loads');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val || 0);
  };

  return (
    <div className="load-data-management">
      {/* Company Selector */}
      <div className="company-selector-row">
        <label>Select Company:</label>
        <select
          value={selectedCompanyId}
          onChange={(e) => { setSelectedCompanyId(e.target.value); setPage(1); }}
          className="company-select"
        >
          <option value="">-- Select a Company --</option>
          {companies.map(company => (
            <option key={company.company_id} value={company.company_id}>
              {company.company_name}
            </option>
          ))}
        </select>
      </div>

      {selectedCompanyId && (
        <>
          {/* Upload Section */}
          <div className="ldm-upload-section">
            <h3>Upload Load Data</h3>
            <p className="ldm-upload-hint">Upload an Excel (.xlsx) file with load data. Existing records with the same Load ID will be updated.</p>
            <div className="ldm-upload-row">
              <input
                id="load-file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setFile(e.target.files[0])}
                className="ldm-file-input"
              />
              <button
                onClick={handleUpload}
                className="btn-primary btn-small"
                disabled={!file || uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>

            {uploadResult && (
              <div className={`ldm-result ${uploadResult.success ? 'success' : 'error'}`}>
                <strong>{uploadResult.success ? 'Success' : 'Error'}:</strong> {uploadResult.message}
                {uploadResult.success && (
                  <div className="ldm-result-details">
                    Processed: {uploadResult.records_processed} | Inserted: {uploadResult.records_inserted} | Replaced: {uploadResult.records_updated}
                  </div>
                )}
                {uploadResult.errors?.length > 0 && (
                  <div className="ldm-result-errors">
                    {uploadResult.errors.map((e, i) => <div key={i}>{e}</div>)}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Loads List */}
          <div className="ldm-list-section">
            <div className="ldm-list-header">
              <h3>Existing Load Data {loads?.total ? `(${loads.total} records)` : ''}</h3>
              {loads?.total > 0 && (
                <button
                  onClick={handleDeleteAll}
                  className="btn-danger btn-small"
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete All'}
                </button>
              )}
            </div>

            {loadingLoads ? (
              <div className="loading">Loading loads...</div>
            ) : !loads?.loads?.length ? (
              <p className="ldm-empty">No load data uploaded yet.</p>
            ) : (
              <>
                <div className="ldm-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Load ID</th>
                        <th>Broker</th>
                        <th>Driver</th>
                        <th>Dispatcher</th>
                        <th>PU Date</th>
                        <th>DEL Date</th>
                        <th>PU State</th>
                        <th>DEL State</th>
                        <th className="text-right">Pay</th>
                        <th className="text-right">Miles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loads.loads.map((load) => (
                        <tr key={load.id}>
                          <td>{load.load_id}</td>
                          <td>{load.broker || '-'}</td>
                          <td>{load.driver_name || '-'}</td>
                          <td>{load.dispatcher || '-'}</td>
                          <td>{formatDate(load.pickup_date)}</td>
                          <td>{formatDate(load.delivery_date)}</td>
                          <td>{load.pickup_state || '-'}</td>
                          <td>{load.delivery_state || '-'}</td>
                          <td className="text-right">{formatCurrency(load.load_pay)}</td>
                          <td className="text-right">{load.total_miles || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {loads.total_pages > 1 && (
                  <div className="ldm-pagination">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="btn-secondary btn-small"
                    >
                      Prev
                    </button>
                    <span>Page {page} of {loads.total_pages}</span>
                    <button
                      onClick={() => setPage(p => Math.min(loads.total_pages, p + 1))}
                      disabled={page >= loads.total_pages}
                      className="btn-secondary btn-small"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default LoadDataManagement;
